<template>
  <hr-form-dialog
    ref="dialog"
    size="lg"
    :title="$t('sessions.modals.scheduleSession.textTitle')"
    @close="hide"
    @hide="onDialogHide"
    @submit="onSubmitForm"
    @validation-error="onValidate"
  >
    <!--
      THE TYPE IS A PROPERTY OF THE PLAN, so it sits with the plan's name rather than inside the form.
      The title identifies the record and the chip states an attribute of that same record; the form
      then opens on the name field, which is what should be first.

      This is NOT a claim that the type reshapes both tabs — only the settings tab branches on it
      today. Should a future tab start to, this placement simply becomes true in that stronger sense
      as well, and nothing here needs revisiting.

      Nearly every plan is a normal session, so the chip stays quiet in that state and takes the
      selected fill in any other: reopening a plan tells you at a glance that it is not an ordinary
      one. The explanation rides in the menu, on the option it explains.
    -->
    <template #title-attributes>
      <session-type-chip
        v-model="sessionType"
        :client="session.client"
      />
    </template>
    <hr-tabs
      v-model="currentTab"
      class="col-12 q-mb-md"
    >
      <hr-tab
        name="settings"
        :label="$t('sessions.modals.scheduleSession.tabSettings')"
      />
      <hr-tab
        name="schedule"
        :label="$t('sessions.modals.scheduleSession.tabSchedule')"
        :disable="!networkOnline || offlineMode || isStaffProfile"
      />
    </hr-tabs>
    <hr-tab-panels
      v-model="currentTab"
      keep-alive
    >
      <!--
        Tight to the divider on purpose: the session type chip is the first thing in here, and
        proximity is what makes it read as chrome scoping the panel rather than the form's first row.
      -->
      <hr-tab-panel
        name="settings"
        class="q-px-none q-pt-sm"
      >
        <session-settings-editor
          ref="sessionSettingsEditor"
          v-model="session"
          :use-session-prototypes="useSessionPrototypes"
        />
      </hr-tab-panel>
      <hr-tab-panel
        v-if="!isStaffProfile"
        name="schedule"
        class="q-px-none"
      >
        <q-card-section
          v-if="connectedClinic.integration"
          :class="{'q-px-none': $q.platform.is.mobile}"
          class="q-pt-none"
        >
          <banner
            :message="$t('sessions.modals.scheduleSession.textUsingPlatform', {
              integrationLabel: getIntegrationName(connectedClinic.integration)
            })"
            class="q-mb-md"
          />
        </q-card-section>
        <q-card-section
          v-else-if="!hasPremiumScheduling"
          :class="{'q-px-none': $q.platform.is.mobile}"
          class="q-pt-none"
        >
          <hr-warning-banner
            :message="canManageSubscription
              ? $t('sessions.modals.scheduleSession.textLegacySchedulingDiscontinuedCanTurnOn')
              : $t('sessions.modals.scheduleSession.textLegacySchedulingDiscontinued')"
            class="q-mb-md"
            data-e2e="banner-legacy-scheduling-discontinued"
          >
            <template #action>
              <hr-supporting-btn
                v-if="canManageSubscription"
                :label="$t('sessions.modals.scheduleSession.buttonTurnOnScheduling')"
                data-e2e="button-legacy-scheduling-turn-on-scheduling"
                @click="onTurnOnScheduling"
              />
              <hr-supporting-btn
                v-else
                :label="$t('sessions.modals.scheduleSession.buttonLearnAboutScheduling')"
                data-e2e="button-legacy-scheduling-learn-more"
                @click="onLearnAboutScheduling"
              />
            </template>
          </hr-warning-banner>
          <q-list
            v-if="isLoadingAppointments"
            bordered
            separator
          >
            <q-expansion-item
              v-for="skeleton in 3"
              :key="skeleton"
              group="appointments"
              header-class="text-dark"
            >
              <template #header>
                <q-item-section
                  avatar
                >
                  <q-skeleton
                    type="rect"
                    width="24px"
                    height="24px"
                    class="bg-secondary"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-section>
                    <q-item-label>
                      <div
                        class="row q-col-gutter-xs"
                      >
                        <div
                          v-for="weekday in 3"
                          :key="weekday"
                        >
                          <q-skeleton
                            type="QBadge"
                            height="16px"
                          />
                        </div>
                      </div>
                    </q-item-label>
                    <q-item-label
                      caption
                      class="row q-col-gutter-sm"
                    >
                      <div>
                        <q-skeleton
                          type="text"
                          width="60px"
                          height="16px"
                        />
                      </div>
                      <div>
                        {{ $t('common.to') }}
                      </div>
                      <div>
                        <q-skeleton
                          type="text"
                          width="60px"
                          height="16px"
                        />
                      </div>
                    </q-item-label>
                  </q-item-section>
                </q-item-section>
                <q-item-section side>
                  <q-skeleton
                    type="QInput"
                    width="120px"
                    height="24px"
                  />
                </q-item-section>
              </template>
            </q-expansion-item>
          </q-list>
          <q-list
            v-else
            bordered
            separator
          >
            <q-expansion-item
              v-for="appointment in appointmentsList"
              :key="appointment.id"
              :default-opened="isDefaultOpenedAppointment"
              group="appointments"
              header-class="text-dark hover-detect"
            >
              <template #header>
                <template
                  v-if="hasAppointment(appointment)"
                >
                  <q-item-section
                    avatar
                  >
                    <q-icon
                      :name="hasRecurringWeekdays(appointment) ? 'event_repeat': 'calendar_month'"
                      color="secondary"
                    />
                  </q-item-section>
                  <q-item-section>
                    <q-item-section>
                      <q-item-label>
                        <div
                          v-if="hasRecurringWeekdays(appointment)"
                          class="row q-col-gutter-xs"
                        >
                          <div
                            v-for="weekday in getWeekdays(appointment)"
                            :key="weekday"
                          >
                            <q-badge
                              color="primary-variant"
                            >
                              {{ weekday }}
                            </q-badge>
                          </div>
                        </div>
                        <template
                          v-else
                        >
                          {{ appointmentDate(appointment) }}
                        </template>
                      </q-item-label>
                      <q-item-label
                        v-if="hasScheduledStartTimestamp(appointment)"
                        caption
                      >
                        {{ formatStartTime(appointment) }}
                        {{ $t('common.to') }}
                        {{ formatFinishTime(appointment) }}
                      </q-item-label>
                    </q-item-section>
                  </q-item-section>
                  <q-item-section side>
                    {{ appointment.userName }}
                  </q-item-section>
                </template>
                <template v-else>
                  <q-item-section
                    avatar
                  >
                    <q-icon
                      name="add"
                      color="secondary"
                    />
                  </q-item-section>
                  <q-item-section>
                    <q-item-section>
                      <q-item-label>
                        {{ $t('sessions.modals.scheduleSession.newAppointment') }}
                      </q-item-label>
                    </q-item-section>
                  </q-item-section>
                </template>
                <q-item-section
                  side
                  class="show-on-hover"
                >
                  <q-btn
                    icon="delete"
                    color="negative"
                    flat
                    round
                    dense
                    @click.stop="onDeleteAppointment(appointment)"
                  />
                </q-item-section>
              </template>
              <q-card>
                <q-card-section>
                  <edit-scheduled-appointment
                    :model-value="appointment"
                    :client="currentClient"
                    :session-id="session.id"
                    @update:model-value="onUpdateAppointment"
                  />
                </q-card-section>
              </q-card>
            </q-expansion-item>
            <!-- REMOVE: SCHEDULING -->
            <q-expansion-item
              :default-opened="appointmentsListIsEmpty"
              group="appointments"
              header-class="text-dark hover-detect"
            >
              <template #header>
                <q-item-section
                  avatar
                >
                  <q-icon
                    name="add"
                    color="secondary"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-section>
                    <q-item-label>
                      {{ $t('sessions.modals.scheduleSession.newAppointment') }}
                    </q-item-label>
                  </q-item-section>
                </q-item-section>
              </template>
              <q-card>
                <q-card-section>
                  <edit-scheduled-appointment
                    v-model="newAppointment"
                    :client="currentClient"
                    :session-id="session.id"
                  />
                  <div class="row justify-end q-mt-md">
                    <div>
                      <q-btn
                        :label="$t('common.buttonAdd')"
                        flat
                        color="primary"
                        :disable="!hasAppointment(newAppointment)"
                        @click="onAddAppointment"
                      />
                      <q-tooltip v-if="!hasAppointment(newAppointment)">
                        {{ $t('sessions.modals.scheduleSession.tooltipNeedToSetStartTime') }}
                      </q-tooltip>
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </q-expansion-item>
            <!-- /REMOVE: SCHEDULING -->
          </q-list>
        </q-card-section>
        <q-card-section
          v-else
          :class="{'q-px-none': $q.platform.is.mobile}"
          class="q-pt-none"
        >
          <banner
            :message="$t('sessions.modals.scheduleSession.textSchedulingMoved')"
            class="q-mb-md"
          />
        </q-card-section>
      </hr-tab-panel>
    </hr-tab-panels>
    <template #actions>
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        type="submit"
        :label="$t('common.buttonSave')"
        data-e2e="button-save"
      />
    </template>
  </hr-form-dialog>
</template>
<script>
import { callFirebaseFunction } from 'boot/firebase';
import EditScheduledAppointment from 'components/Sessions/Modals/EditScheduledAppointment';
import SessionSettingsEditor from 'components/Sessions/Modals/SessionSettingsEditor';
import SessionTypeChip from 'components/Sessions/SessionTypeChip';
import Banner from 'components/UI/Banner';
import { Timestamp } from 'firebase/firestore';
import {
  chain, findIndex, get, has, isArray, isEmpty, map, omit,
  reject,
} from 'lodash';
import { mapState } from 'pinia';
import { uid } from 'quasar';
import { useTime } from 'src/composables/time';
import { POLLING_DRIVEN_INTEGRATIONS } from 'src/constants/integrations';
import {
  HrConfirmBtn, HrDismissBtn, HrFormDialog, HrSupportingBtn, HrTab, HrTabPanel, HrTabPanels, HrTabs,
  HrWarningBanner,
} from 'src/design-system';
import AppointmentsDB from 'src/fb/appointments-db';
import SessionsDB from 'src/fb/sessions-db';
import Auth from 'src/mixins/Auth';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';

const SCHEDULING_PRODUCT_URL = 'https://hirasmus.com/product/scheduling/';

export default {
  name: 'ScheduleSession',

  components: {
    EditScheduledAppointment,
    SessionSettingsEditor,
    SessionTypeChip,
    Banner,
    HrConfirmBtn,
    HrDismissBtn,
    HrFormDialog,
    HrTab,
    HrTabPanel,
    HrTabPanels,
    HrSupportingBtn,
    HrTabs,
    HrWarningBanner,
  },

  mixins: [Auth, Integrations, Interactions],

  props: {
    value: {
      type: Object,
      required: true,
    },

    tab: {
      type: String,
      default: 'settings',
    },
  },

  emits: ['hide', 'ok'],

  setup() {
    const { getLocaleTimeString, addToDate, getLocaleDateString } = useTime();
    return { getLocaleTimeString, addToDate, getLocaleDateString };
  },

  data() {
    return {
      currentTab: 'settings',

      session: {},

      isLoadingAppointments: false,

      appointments: [],
      newAppointment: {},
      appointmentColumns: [
        {
          name: 'date',
          align: 'left',
          sortable: true,
        },
        {
          name: 'from',
          label: this.$t('sessions.modals.scheduleSession.timeInputLabel'),
          align: 'left',
          field: 'scheduledStartTimestamp',
          format: (val, appointment) => this.formatStartTime(appointment),
        },
        {
          name: 'to',
          label: this.$t('sessions.modals.scheduleSession.timeFinishInputLabel'),
          align: 'left',
          field: 'duration',
          format: (val, appointment) => this.formatFinishTime(appointment),
        },
        {
          name: 'user',
          label: this.$t('sessions.modals.scheduleSession.therapistLabel'),
          align: 'left',
          field: 'userName',
          sortable: true,
        },
      ],
    };
  },

  computed: {
    ...mapState(auth, [
      'userDetails',
      'currentClient',
      'connectedClinic',
      'useSessionPrototypes',
      'hasPremiumScheduling',
      'isManager',
    ]),
    ...mapState(network, ['networkOnline', 'offlineMode']),

    // The same rule that shows the Subscription menu item: managers only, and never in the native app.
    canManageSubscription() {
      return this.isManager && process.env.MODE !== 'capacitor';
    },

    sessionType: {
      get() {
        return this.session.sessionType ?? 'normal';
      },
      set(sessionType) {
        this.session = { ...this.session, sessionType };
      },
    }, // sessionType()

    hasAppointments() {
      return !chain(this.appointments).reject({ editStatus: 'delete' }).isEmpty().value();
    },

    isStaffProfile() {
      return get(this.currentClient, 'type') === 'staff';
    },

    isDefaultOpenedAppointment() {
      return chain(this.appointments).reject({ editStatus: 'delete' }).isEmpty().value();
    },

    appointmentsList() {
      return chain(this.appointments)
        .reject({ editStatus: 'delete' })
        /* Remove when releasing scheduling: */
        .sortBy((appointment) => (
          this.hasRecurringWeekdays(appointment) ? 0
            : get(appointment, 'scheduledStartTimestamp')
        ))
        /* /Remove when releasing scheduling */
        /* Add when releasing scheduling: */
        // .filter((appointment) => this.hasRecurringWeekdays(appointment))
        /* /Add when releasing scheduling */
        .value();
    },

    appointmentsListIsEmpty() {
      return isEmpty(this.appointments);
    },
  },

  watch: {
    value: {
      deep: false,
      immediate: true,
      async handler(session) {
        this.session = {
          version: 2,
          sessionName: '',
          scheduleType: 'recurring',
          scheduledTimestamp: Timestamp.now(),
          recurringWeekdays: [],
          recurringTime: null,

          limitedVisibility: false,
          limitHistoryVisibility: false,

          notes: '',
          shuffle: false,
          sessionType: 'normal',
          status: 'scheduled',
          sessionNoteTemplate: null,
          supervisionNoteTemplate: null,

          ...session,
          createdBy: this.userDetails.userId,
          createdByName: this.userDetails.name,
          // @todo Visibility was originally in Object so we have to convert
          // old-style data... for now...
          visibility: isArray(session.visibility) ? session.visibility : [],
        };
        if ((session.id) && (session.client) && (session.clinic)) {
          this.loadAppointments(session.client, session.clinic, session.id);
        }
      },
    },

    tab: {
      immediate: true,
      handler(newTab) {
        this.currentTab = newTab;
      },
    },
  },

  methods: {

    show() {
      this.resetNewAppointment();
      this.$refs.dialog.show();
    },

    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      this.$emit('hide');
    },

    onTurnOnScheduling() {
      // HrFormDialog sets no-route-dismiss, so the dialog would stay open over the Subscription page.
      this.hide();
      this.$router.push({ name: 'Settings - Subscription' });
    },

    onLearnAboutScheduling() {
      window.open(SCHEDULING_PRODUCT_URL, '_blank', 'noopener');
    },

    onValidate() {
      this.notifyNegative(this.$t('sessions.noteTemplate.validateAllSessionNoteFields'));
      this.currentTab = 'settings';
    },

    async onSubmitForm() {
      this.$q.loading.show();

      try {
        let sessionId = null;
        const sessionsDb = new SessionsDB();
        if (has(this.session, 'sessionPrograms')) {
          const sessionInfo = { ...this.session, isDeleted: false };
          sessionId = await sessionsDb.createSession(sessionInfo, this.session.sessionPrograms);
        } else if (get(this.value, 'id')) {
          sessionId = this.value.id;
          await sessionsDb.set(omit(this.session, 'id'), sessionId);
        }
        if (sessionId) {
          await this.saveAppointments(sessionId);
        }

        const shouldUpdateAutomapping = this.connectedClinic.integration
          && !POLLING_DRIVEN_INTEGRATIONS.includes(this.connectedClinic.integration)
          && this.value.sessionName !== this.session.sessionName;

        if (shouldUpdateAutomapping && !this.offlineMode) {
          // update automapping
          await callFirebaseFunction('mapSessionWithAppointments', {
            clientId: this.currentClient.id,
          });
        } else if (shouldUpdateAutomapping && this.offlineMode) {
          const clientsMappingForSync = JSON.parse(localStorage.getItem('clientsMappingForSync')) || [];

          if (!clientsMappingForSync.includes(this.currentClient.id)) {
            clientsMappingForSync.push(this.currentClient.id);
            localStorage.setItem('clientsMappingForSync', JSON.stringify(clientsMappingForSync));
          }
        }

        this.$emit('ok');
      } finally {
        this.$q.loading.hide();
        this.hide();
      }
    }, // onSubmitForm()

    onAddAppointment() {
      this.addAppointment(this.newAppointment);
      this.resetNewAppointment();
      this.notifySuccess(this.$t('sessions.modals.scheduleSession.notifyAddAppointment'));
    },

    onUpdateAppointment(appointment) {
      const idx = findIndex(this.appointments, { id: appointment.id });
      if (idx !== -1) {
        this.appointments.splice(idx, 1, {
          ...appointment,
          editStatus: (appointment.editStatus === 'create') ? 'create' : 'update',
        });
      }
    }, // onUpdateAppointment

    onDeleteAppointment(appointment) {
      this.confirmDialog(
        null,
        this.$t('sessions.modals.scheduleSession.confirmDeleteAppointment'),
        () => {
          const idx = findIndex(this.appointments, { id: appointment.id });
          if (idx !== -1) {
            if (get(appointment, 'editStatus') === 'create') {
              this.appointments = reject(this.appointments, { id: appointment.id });
            } else {
              this.appointments = map(
                this.appointments,
                (app) => ((app.id === appointment.id) ? { ...app, editStatus: 'delete' } : app),
              );
            }
          }
        },
      );
    }, // onDeleteAppointment(appointment)

    addAppointment(appointment) {
      this.appointments = [
        ...this.appointments,
        {
          ...appointment,
          id: uid(),
          editStatus: 'create',
        },
      ];
    }, // addAppointment(appointment

    resetNewAppointment() {
      this.newAppointment = {
        scheduledStartTimestamp: null,
        duration: 60 * 60,
        recurringWeekdays: [],
        client: this.currentClient.id,
        clinic: this.userDetails.clinic,
        user: get(this.newAppointment, 'user', this.userDetails.userId),
        userName: get(this.newAppointment, 'userName', this.userDetails.name),
        status: 'scheduled',
      };
    },

    async loadAppointments(clientId, clinicId, sessionId) {
      this.isLoadingAppointments = true;
      const appointmentsDb = new AppointmentsDB();
      try {
        this.appointments = await appointmentsDb.readAll([
          ['clinic', '==', this.userDetails.clinic],
          ['session', '==', sessionId],
        ]);
      } finally {
        this.isLoadingAppointments = false;
      }
    }, // loadAppointments()

    saveAppointments(sessionId) {
      if (this.hasAppointment(this.newAppointment)) {
        this.addAppointment(this.newAppointment);
      }

      const appointmentsDb = new AppointmentsDB();
      const saveActions = chain(this.appointments)
        .map((appointment) => {
          if (!this.hasScheduledStartTimestamp(appointment)) {
            return null;
          }
          switch (appointment.editStatus) {
            case 'create': {
              const newAppointmentObject = omit(
                { ...appointment, session: sessionId, isDeletedSession: false },
                ['id', 'editStatus'],
              );

              return appointmentsDb.create(newAppointmentObject);
            }
            case 'update': {
              return appointmentsDb.set(
                {
                  ...omit(appointment, ['id', 'editStatus']),
                  session: sessionId,
                },
                appointment.id,
              );
            }
            case 'delete': {
              return appointmentsDb.delete(appointment.id);
            }
            default:
              return null;
          }
        })
        .value();
      return Promise.all(saveActions);
    }, // saveAppointments()

    getWeekdays(appointment) {
      return chain(appointment)
        .get('recurringWeekdays')
        .sort()
        .map((weekday) => get(this.$tm('common.weekdays'), weekday))
        .value();
    }, // getWeekdays()

    formatStartTime(appointment) {
      const scheduledStartTimestamp = get(appointment, 'scheduledStartTimestamp');

      if (!(scheduledStartTimestamp instanceof Timestamp)) {
        return null;
      }
      return this.getLocaleTimeString(scheduledStartTimestamp, { options: { timeStyle: 'short' } });
    }, // formatStartTime()

    formatFinishTime(appointment) {
      const scheduledStartTimestamp = get(appointment, 'scheduledStartTimestamp');
      const duration = get(appointment, 'duration', null);

      if ((!(scheduledStartTimestamp instanceof Timestamp))
         || (duration === null)) {
        return null;
      }
      const finishDate = this.addToDate(scheduledStartTimestamp.toDate(), { seconds: duration });
      return this.getLocaleTimeString(finishDate, { options: { timeStyle: 'short' } });
    }, // formatFinishTime()

    appointmentDate(appointment) {
      const scheduledStartTimestamp = get(appointment, 'scheduledStartTimestamp');
      if (!(scheduledStartTimestamp instanceof Timestamp)) {
        return null;
      }
      return this.getLocaleDateString(scheduledStartTimestamp);
    }, // appointmentDate()

    hasAppointment(appointment) {
      return this.hasScheduledStartTimestamp(appointment);
    },

    hasScheduledStartTimestamp(appointment) {
      const scheduledStartTimestamp = get(appointment, 'scheduledStartTimestamp');
      return scheduledStartTimestamp instanceof Timestamp;
    },

    hasRecurringWeekdays(appointment) {
      return !chain(appointment).get('recurringWeekdays').isEmpty().value();
    },

  },
};
</script>
<style lang="scss" scoped>
.hover-detect .show-on-hover {
  visibility: hidden;
}
.hover-detect:hover .show-on-hover {
  visibility: visible;
}

</style>
